import React, { useState } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createAgency, updateAgency } from '../../api/agencyApi';
import { Agency } from '../../types/agency';
import { cnpjMask, validaCNPJ } from '../../utils';

interface AddModalProps {
    agency: Agency|null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Agency) => void;
    onUpdateData: (newData: Agency) => void;
}

const AddEditAgencyModal = ({ agency, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const toggleAddModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: agency ? agency.id : '',
            name: agency ? agency.name : '',
            cnpj: agency ? agency.cnpj : '',
            site: agency ? agency.site : '',
            // users: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required('O nome é obrigatório'),
            cnpj: Yup.string()
                .required('CNPJ é obrigatório')
                .test('is-valid-cnpj', 'CNPJ inválido', value => validaCNPJ(value)),
            site: Yup.string().trim().required('O site é obrigatório'),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            if (agency) {
                updateAgency(values)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Agência atualizada com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAgency(values)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Agência cadastrada com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    {agency ? 'Editar' : 'Adicionar'} agência
                </ModalHeader>

                <ModalBody>
                    <div className="mb-3">
                        <Label
                            htmlFor="name"
                            className="form-label"
                        >
                            Nome
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                            invalid={
                                !!(handleForm.touched.name && handleForm.errors.name)
                            }
                        />
                        {handleForm.touched.name && handleForm.errors.name ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.name}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="cnpj"
                            className="form-label"
                        >
                            CNPJ
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="cnpj"
                            name="cnpj"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={cnpjMask(handleForm.values.cnpj)}
                            invalid={
                                !!(handleForm.touched.cnpj && handleForm.errors.cnpj)
                            }
                        />
                        {handleForm.touched.cnpj && handleForm.errors.cnpj ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.cnpj}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="site"
                            className="form-label"
                        >
                            Site
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="site"
                            name="site"
                            placeholder={"exemplo.com"}
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.site}
                            invalid={
                                !!(handleForm.touched.site && handleForm.errors.site)
                            }
                        />
                        {handleForm.touched.site && handleForm.errors.site ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.site}</FormFeedback>
                        ) : null}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {agency ? 'Editar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditAgencyModal;